import { action, computed, observable, toJS } from 'mobx';
import { TagType } from '@linktivity/link-ui';
import { activityApi, pricingApi } from '@link/supplier/services';
import {
  PublishTarget,
  TemplateStatus,
  SalesStatus
} from '@link/supplier/services/const';
import {
  ActivityTemplate,
  EditActivityTemplateReqPayload,
  PlanTemplate,
  PriceGroup,
  PriceSetting,
  AdministrativeActivityFields
} from '@link/supplier/services/types';
import { deepMergeOverriding } from '@link/supplier/utils';

// protobuf 的 "one-of"（联合）字段：必须整体取导入数据，而不能深合并 —— 合并 one-of 的两个分支
// 会导致两者同时存在。以后发现新的 one-of 字段，往这里加字段名即可。
const ACTIVITY_ONE_OF_FIELDS = ['bookingRule'];
const PLAN_ONE_OF_FIELDS = ['voucherExpirationPeriod', 'bookingRule'];

// 已发布状态：PUBLISHED 仅 NEXT（现行系统）、NEO_PUBLISHED 仅 NEO、BOTH_PUBLISHED 两者都已发布
const PUBLISHED_STATUSES: TemplateStatus[] = [
  TemplateStatus.Published,
  TemplateStatus.NeoPublished,
  TemplateStatus.BothPublished
];

// PriceInfo.startTimestamp/endTimestamp 在 SDK 里是 Date 类型（价格的「可预订时间窗」，
// 与 dateSpec 的活动日期是不同维度，不能丢）。经过导出→JSON.parse 往返后它们会变成字符串，
// 未设置时则是空对象 {}，直接发送会触发 SDK 序列化的 toISOString() 报错。
// 这里把可解析的值转回 Date，未设置/无法解析则返回 undefined（由调用方删除该字段）。
const toTimestamp = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === 'string' && value) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
};

export class Activity {
  @observable accessor activity: ActivityTemplate | null = null;

  @action
  async getActivity(activityId: string) {
    const activity = await activityApi.getActivityTemplate({
      body: {
        templateId: activityId
      }
    });
    if (activity) {
      this.setActivity(activity);
    }
  }

  @action
  async mergeActivity(activity: ActivityTemplate) {
    // 活动未加载时不导入（否则会带着空的 activityTemplateId 发请求）
    if (!this.activity) {
      return false;
    }
    // 已存在 plans 时禁止导入
    if ((this.activity.plans?.length ?? 0) > 0) {
      return false;
    }

    // 1. 先用导入数据更新活动本体，此时忽略 plans
    const mergedActivity = deepMergeOverriding(
      this.activity,
      { ...activity, plans: [] }, // 首次更新先去掉导入数据里的 plans
      ACTIVITY_ONE_OF_FIELDS
    );
    await activityApi.editActivityTemplate({
      body: {
        activityTemplateId: this.activityId,
        payload: { ...mergedActivity },
        activityVersion: this.version
      }
    });

    // 2. 导入活动级价格组（不在 editActivityTemplate 的 payload 里，需单独调价格组接口）。
    // 必须在处理 plans 之前完成，否则 priceInfos 引用的价格组还不存在。
    const priceGroupIdMap = await this.importPriceGroups(activity.priceSetting);

    // 3. 活动更新完成后再处理 plans
    if (activity.plans?.length) {
      for (let i = 0; i < activity.plans.length; i++) {
        const importedPlan = activity.plans[i];
        const title = importedPlan?.title || '';
        // 先用导入 plan 的标题创建一个新 plan
        let currentPlan = await activityApi.createPlanTemplate({
          body: {
            activityTemplateId: this.activityId,
            title
          }
        });
        // 同时导入 units 和 priceInfos
        const importedUnits = importedPlan.priceSetting?.units;
        const importedPriceInfos = importedPlan.priceSetting?.priceInfos?.map(
          info => {
            const cleaned = { ...info };
            // 预订时间窗需保留：能解析则转回 Date，未设置则删除以避开序列化报错（见 toTimestamp）
            const startTimestamp = toTimestamp(cleaned.startTimestamp);
            const endTimestamp = toTimestamp(cleaned.endTimestamp);
            if (startTimestamp) {
              cleaned.startTimestamp = startTimestamp;
            } else {
              delete cleaned.startTimestamp;
            }
            if (endTimestamp) {
              cleaned.endTimestamp = endTimestamp;
            } else {
              delete cleaned.endTimestamp;
            }
            // 价格组 id 可能在目标活动里被重新分配，按映射修正引用
            cleaned.priceGroups = cleaned.priceGroups?.map(group => ({
              ...group,
              id: group.id ? (priceGroupIdMap[group.id] ?? group.id) : group.id
            }));
            return cleaned;
          }
        );
        if (importedUnits?.length || importedPriceInfos?.length) {
          const updatedPlan = await pricingApi.editPlanPriceInfo({
            body: {
              planTemplateId: currentPlan.id!,
              payload: {
                units: importedUnits,
                priceInfos: importedPriceInfos
              },
              planVersion: currentPlan.planVersion
            }
          });
          if (updatedPlan) {
            currentPlan = updatedPlan;
          }
        }
        // 把导入的 plan 数据合并到新建的 plan 上并保存。
        // one-of 字段（voucherExpirationPeriod、bookingRule）整体覆盖 —— 见 PLAN_ONE_OF_FIELDS。
        const mergedPlan = deepMergeOverriding(
          currentPlan,
          importedPlan,
          PLAN_ONE_OF_FIELDS
        );
        // priceSetting 已由上面的 editPlanPriceInfo 处理
        delete mergedPlan.priceSetting;
        await activityApi.editPlanTemplate({
          body: {
            planTemplateId: currentPlan.id!,
            payload: { ...mergedPlan },
            planVersion: currentPlan.planVersion
          }
        });
      }
    }
    // 重新加载活动
    this.reloadActivity();

    return true;
  }

  @action
  async reloadActivity() {
    if (this.activityId) {
      await this.getActivity(this.activityId);
    }
  }

  @action
  async updateActivity(payload: EditActivityTemplateReqPayload) {
    const activity = await activityApi.editActivityTemplate({
      body: {
        activityTemplateId: this.activityId,
        payload: { ...this.activity, ...payload },
        activityVersion: this.version
      }
    });
    if (activity) {
      await this.reloadActivity();
    }
  }

  @action
  updateActivityStatus(status: TemplateStatus) {
    if (this.activity) {
      this.activity.status = status;
    }
  }

  // 发布成功后按发布目标与当前已发布的系统合并出新状态
  @action
  markPublished(target: PublishTarget) {
    const status = this.activity?.status;
    const neo =
      target !== PublishTarget.PublishTargetNext ||
      status === TemplateStatus.NeoPublished ||
      status === TemplateStatus.BothPublished;
    const next =
      target !== PublishTarget.PublishTargetNeo ||
      status === TemplateStatus.Published ||
      status === TemplateStatus.BothPublished;
    if (neo && next) {
      this.updateActivityStatus(TemplateStatus.BothPublished);
    } else if (neo) {
      this.updateActivityStatus(TemplateStatus.NeoPublished);
    } else {
      this.updateActivityStatus(TemplateStatus.Published);
    }
  }

  @action
  setActivityWithoutPlans(activity: ActivityTemplate) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { plans, ...updatedActivity } = activity;
    this.activity = { ...this.activity, ...updatedActivity };
  }

  @action
  updatePlan(plan: PlanTemplate) {
    const currentPlanIndex =
      this.activity?.plans?.findIndex(p => p.id === plan.id) ?? -1;
    if (currentPlanIndex >= 0 && this.activity?.plans) {
      this.activity.plans[currentPlanIndex] = plan;
    }
  }

  @action
  setActivity(activity: ActivityTemplate) {
    this.activity = activity;
  }

  @action
  clearActivity() {
    this.activity = null;
  }

  @action
  async createPlan(title: string) {
    const plan = await activityApi.createPlanTemplate({
      body: {
        activityTemplateId: this.activityId,
        title
      }
    });
    if (plan) {
      this.addPlan(plan);
    }
  }

  @action
  async duplicatePlan(title: string, sourcePlanId: string) {
    const plan = await activityApi.copyPlanTemplate({
      body: {
        activityTemplateId: this.activityId,
        sourcePlanTemplateId: sourcePlanId,
        title
      }
    });
    if (plan) {
      this.addPlan(plan);
    }
  }

  @action
  addPlan(plan: PlanTemplate) {
    if (plan) {
      this.activity?.plans?.push(plan);
    }
  }

  @action
  async addPriceGroup(name: string) {
    const activity = await pricingApi.createPriceGroup({
      body: {
        templateId: this.activityId,
        name,
        settingVersion: this.settingVersion
      }
    });
    if (activity) {
      this.setActivityWithoutPlans(activity);
    }
  }

  @action
  async updatePriceGroup(priceGroups: Array<PriceGroup>, currency: string) {
    const priceSetting = {
      ...this.priceSetting,
      priceGroups,
      currency
    };
    const activity = await pricingApi.editPriceGroup({
      body: {
        templateId: this.activityId,
        priceSetting,
        settingVersion: this.settingVersion
      }
    });
    if (activity) {
      await this.reloadActivity();
    }
  }

  // 导入活动级价格组：activity 级 priceSetting 不在 editActivityTemplate 的 payload 里，
  // 价格组必须走独立接口。对目标活动尚不存在的价格组用 createPriceGroup 创建（后端分配 id），
  // 再用 editPriceGroup 写全 name/otaIds/currency。返回「导入 id -> 实际 id」映射，
  // 供 priceInfos 修正价格组引用（防止后端分配的 id 与导入 id 不一致）。
  @action
  async importPriceGroups(
    priceSetting?: PriceSetting
  ): Promise<Record<string, string>> {
    const idMap: Record<string, string> = {};
    const importedGroups = priceSetting?.priceGroups;
    if (!importedGroups?.length) {
      return idMap;
    }
    // 每次改动都会更新 settingVersion，需在多次调用间接力传递最新值
    let settingVersion = this.settingVersion;
    const knownIds = new Set(this.priceGroups.map(group => group.id));
    for (const group of importedGroups) {
      if (group.id && knownIds.has(group.id)) {
        idMap[group.id] = group.id; // 已存在（通常是默认组），沿用原 id
        continue;
      }
      const activity = await pricingApi.createPriceGroup({
        body: {
          templateId: this.activityId,
          name: group.name,
          settingVersion
        }
      });
      // 仅在拿到有效值时更新，避免响应缺失 settingVersion 时覆盖成 undefined
      // 导致后续 createPriceGroup/editPriceGroup 携带非法版本
      settingVersion = activity?.priceSetting?.settingVersion ?? settingVersion;
      const created = activity?.priceSetting?.priceGroups?.find(
        item => item.id && !knownIds.has(item.id)
      );
      if (created?.id) {
        knownIds.add(created.id);
        if (group.id) {
          idMap[group.id] = created.id;
        }
      }
    }
    // createPriceGroup 只设置了 name，这里用完整列表补齐 otaIds/currency
    await pricingApi.editPriceGroup({
      body: {
        templateId: this.activityId,
        priceSetting: {
          currency: priceSetting?.currency,
          offedOtas: priceSetting?.offedOtas,
          priceGroups: importedGroups.map(group => ({
            ...group,
            // 映射缺失时回退到原 id，避免写成 undefined 导致 payload 非法
            id: group.id ? (idMap[group.id] ?? group.id) : group.id
          }))
        },
        settingVersion
      }
    });
    return idMap;
  }

  @action
  async updateAdminFields(payload: AdministrativeActivityFields) {
    const activity = await activityApi.editActivityAdministrativeFields({
      body: {
        activityTemplateId: this.activityId,
        payload: { ...this.administrativeFields, ...payload },
        activityVersion: this.version
      }
    });
    if (activity) {
      await this.reloadActivity();
    }
  }

  @computed
  get loaded(): boolean {
    return !!this.activity?.id;
  }

  @computed
  get status() {
    return this.activity?.status || TemplateStatus.Draft;
  }

  @computed
  get isPublished() {
    return PUBLISHED_STATUSES.includes(this.status);
  }

  @computed
  get salesStatus() {
    return this.activity?.salesStatus || SalesStatus.On;
  }

  @computed
  get hideFromSearch() {
    return this.activity?.miscSettings?.hideFromSearch || false;
  }

  @computed
  get statusType() {
    switch (this.activity?.status) {
      case TemplateStatus.Draft: {
        return TagType.info;
      }
      case TemplateStatus.Reviewing: {
        return TagType.danger;
      }
      case TemplateStatus.Approved: {
        return TagType.danger;
      }
      case TemplateStatus.Published:
      case TemplateStatus.NeoPublished:
      case TemplateStatus.BothPublished: {
        return TagType.primary;
      }
      default: {
        return TagType.info;
      }
    }
  }

  @computed
  get title() {
    return this.activity?.title || '';
  }

  @computed
  get bookingType() {
    return this.activity?.bookingType || '';
  }

  @computed
  get localizedTitles() {
    return toJS(this.activity?.localizedTitles) || [];
  }

  @computed
  get description() {
    return this.activity?.description || '';
  }

  @computed
  get localizedDescriptions() {
    return toJS(this.activity?.localizedDescriptions) || [];
  }

  @computed
  get supplierId() {
    return this.activity?.supplierId || '';
  }

  @computed
  get activityId() {
    return this.activity?.id || '';
  }

  @computed
  get timeZone() {
    return this.activity?.timeZone || '';
  }

  @computed
  get administrativeFields() {
    return toJS(this.activity?.administrativeFields) || {};
  }

  @computed
  get notificationSetting() {
    return toJS(this.activity?.notificationSetting) || {};
  }

  @computed
  get plans() {
    return toJS(this.activity?.plans) || [];
  }

  @computed
  get origins() {
    return toJS(this.activity?.origins) || [];
  }

  @computed
  get destinations() {
    return toJS(this.activity?.destinations) || [];
  }

  @computed
  get checklists() {
    return toJS(this.activity?.checklists) || [];
  }

  @computed
  get universalChecklist() {
    return !!this.activity?.universalChecklist;
  }

  @computed
  get checkinPoints() {
    return toJS(this.activity?.checkinPoints) || [];
  }

  @computed
  get universalCheckinPoint() {
    return !!this.activity?.universalCheckinPoint;
  }

  @computed
  get checkoutPoints() {
    return toJS(this.activity?.checkoutPoints) || [];
  }

  @computed
  get universalCheckoutPoint() {
    return !!this.activity?.universalCheckoutPoint;
  }

  @computed
  get pickupLocations() {
    return toJS(this.activity?.pickupLocations) || [];
  }

  @computed
  get universalPickupLocation() {
    return !!this.activity?.universalPickupLocation;
  }

  @computed
  get dropoffLocations() {
    return toJS(this.activity?.dropoffLocations) || [];
  }

  @computed
  get universalDropoffLocation() {
    return !!this.activity?.universalDropoffLocation;
  }

  @computed
  get languages() {
    return toJS(this.activity?.languages) || [];
  }

  @computed
  get cancellationPolicy() {
    return toJS(this.activity?.cancellationPolicy) || {};
  }

  @computed
  get bookingRule() {
    return toJS(this.activity?.bookingRule) || {};
  }

  @computed
  get universalBookingRule() {
    return !!this.activity?.universalBookingRule;
  }

  @computed
  get images() {
    return toJS(this.activity?.images) || [];
  }

  @computed
  get category() {
    return toJS(this.activity?.category) || [];
  }

  @computed
  get ticketSetting() {
    return toJS(this.activity?.ticketSetting) || {};
  }

  @computed
  get priceSetting() {
    return toJS(this.activity?.priceSetting) || {};
  }

  @computed
  get priceGroups() {
    return toJS(this.activity?.priceSetting?.priceGroups) || [];
  }

  @computed
  get faqs() {
    return toJS(this.activity?.faqs) || [];
  }

  @computed
  get universalFaq() {
    return !!this.activity?.universalFaq;
  }

  @computed
  get version() {
    return this.activity?.activityVersion;
  }

  @computed
  get settingVersion() {
    return this.activity?.priceSetting?.settingVersion;
  }
  @computed
  get currency() {
    return this.activity?.priceSetting?.currency || '';
  }
  @computed
  get voucherPreviews() {
    return toJS(this.activity?.voucherPreviews) || [];
  }
}

export default new Activity();
